import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Star, CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import { Alert } from '@/client/components/ui/alert';
import { api } from '@/client/lib/api';
import { StarRatingDisplay, StarRatingInput } from './StarRating';

type MyReview = {
  id: string;
  rating: number;
  title: string | null;
  body: string | null;
  approved: boolean;
  rejected: boolean;
  createdAt: number;
} | null;

// Submit-review surface shown on the portal course detail when the course is
// completed. Three states:
//   1) No review yet: CTA + form to submit
//   2) Pending (or rejected — UX shows pending either way per Sprint 7): badge
//   3) Approved: read-only display of the user's published review
//
// Per Sprint 7 acceptance: reviews don't trigger an "approved" email — the
// user finds out by visiting their portal again.
export function SubmitReviewCard({ enrollmentId }: { enrollmentId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const myReview = useQuery<MyReview>({
    queryKey: ['portal', 'review', enrollmentId],
    queryFn: () => api<MyReview>(`/api/v1/reviews/me/${enrollmentId}`),
    retry: false,
  });

  const submit = useMutation({
    mutationFn: () =>
      api<MyReview>('/api/v1/reviews', {
        method: 'POST',
        body: { enrollmentId, rating, title: title || null, body: body || null },
      }),
    onSuccess: () => {
      setShowForm(false);
      setErrorMsg(null);
      queryClient.invalidateQueries({ queryKey: ['portal', 'review', enrollmentId] });
    },
    onError: (err) => {
      setErrorMsg(err instanceof Error ? err.message : t('errors.generic'));
    },
  });

  if (myReview.isLoading) return null;

  const review = myReview.data;

  if (review?.approved) {
    return (
      <div className="dr-card-static p-6 sm:p-8">
        <span className="inline-flex items-center gap-1.5 rounded-pill border border-success/25 bg-success/10 px-3 py-1 font-sans text-caption font-medium text-success">
          <CheckCircle2 size={12} />
          {t('reviews.your_review_approved_label')}
        </span>
        <div className="mt-4 flex items-center gap-3">
          <StarRatingDisplay value={review.rating} size={22} />
        </div>
        {review.title && (
          <h3 className="mt-3 font-display text-h4 text-primary-dark">{review.title}</h3>
        )}
        {review.body && (
          <p className="mt-2 font-sans text-body text-text-secondary leading-relaxed whitespace-pre-line">
            {review.body}
          </p>
        )}
      </div>
    );
  }

  if (review) {
    // Pending or rejected — both surfaced as pending per Sprint 7 doc.
    return (
      <div className="dr-card-static p-6 sm:p-8">
        <span className="inline-flex items-center gap-1.5 rounded-pill border border-primary/25 bg-primary-light/30 px-3 py-1 font-sans text-caption font-medium text-primary-dark">
          <Sparkles size={12} />
          {t('reviews.your_review_pending_label')}
        </span>
        <div className="mt-4 flex items-center gap-3">
          <StarRatingDisplay value={review.rating} size={22} />
        </div>
        {review.title && (
          <h3 className="mt-3 font-display text-h4 text-primary-dark">{review.title}</h3>
        )}
        {review.body && (
          <p className="mt-2 font-sans text-body text-text-secondary leading-relaxed whitespace-pre-line">
            {review.body}
          </p>
        )}
        <p className="mt-4 font-sans text-caption text-text-muted">
          {t('reviews.pending_help')}
        </p>
      </div>
    );
  }

  if (!showForm) {
    return (
      <div className="dr-card-static p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
        <div className="flex items-start gap-4 min-w-0">
          <span className="shrink-0 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-warning/20 to-warning/5 border border-warning/20 text-warning">
            <Star size={20} fill="currentColor" strokeWidth={0} />
          </span>
          <div className="min-w-0">
            <h3 className="font-display text-h4 text-primary-dark">{t('reviews.cta_title')}</h3>
            <p className="mt-1 font-sans text-body-small text-text-secondary leading-relaxed">
              {t('reviews.cta_body')}
            </p>
          </div>
        </div>
        <Button onClick={() => setShowForm(true)} className="shrink-0">
          {t('reviews.cta_button')}
        </Button>
      </div>
    );
  }

  return (
    <div className="dr-card-static p-6 sm:p-8">
      <p className="dr-eyebrow">
        {t('reviews.form_eyebrow', { defaultValue: 'Nové hodnotenie' })}
      </p>
      <h3 className="mt-3 font-display text-h3 text-primary-dark">{t('reviews.form_title')}</h3>
      <p className="mt-2 font-sans text-body-small text-text-secondary leading-relaxed">
        {t('reviews.form_help')}
      </p>

      <div className="mt-6">
        <Label>{t('reviews.label_rating')}</Label>
        <div className="mt-1">
          <StarRatingInput value={rating} onChange={setRating} disabled={submit.isPending} />
        </div>
      </div>

      <div className="mt-5">
        <Label htmlFor="review-title">{t('reviews.label_title')}</Label>
        <Input
          id="review-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          disabled={submit.isPending}
        />
      </div>

      <div className="mt-5">
        <Label htmlFor="review-body">{t('reviews.label_body')}</Label>
        <textarea
          id="review-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={2000}
          disabled={submit.isPending}
          rows={5}
          className="block w-full rounded-md bg-white/70 backdrop-blur-md border border-glass-border-strong px-4 py-3 font-sans text-body text-text-primary placeholder:text-text-muted hover:border-primary/30 focus-visible:outline-none focus-visible:border-primary focus-visible:bg-white focus-visible:shadow-glow transition-all disabled:opacity-55 disabled:cursor-not-allowed"
        />
        <p className="mt-1.5 font-sans text-caption text-text-muted text-right">
          {body.length} / 2000
        </p>
      </div>

      {errorMsg && (
        <Alert variant="error" className="mt-4">
          {errorMsg}
        </Alert>
      )}

      <div className="mt-7 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
        <Button
          variant="ghost"
          onClick={() => {
            setShowForm(false);
            setErrorMsg(null);
          }}
          disabled={submit.isPending}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => {
            if (rating < 1) {
              setErrorMsg(t('reviews.error_rating_required'));
              return;
            }
            submit.mutate();
          }}
          disabled={submit.isPending || rating < 1}
        >
          {submit.isPending ? t('common.loading') : t('reviews.submit_button')}
        </Button>
      </div>
    </div>
  );
}
